import React, { useEffect, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import QRCode from 'react-qr-code';

import { Spacing, Colors, Dimensions } from '@/shared/theme';
import { Button, Text, CenteredColumn } from '@/shared/ui';
import { ResultTitle } from '@/shared/resultText';
import { useAnalytics, useOtpLink } from '@/hooks';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useDownloadSMS } from '@/hooks/useDownloadSMS';
import { env } from '@/env';
import { VIEW_DOWNLOAD_APP_QR, TEXT_ME_LINK } from '@/consts/events';

// Logo
import LogoIcon from '@/assets/images/happy-hearts-logo.svg';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${Spacing.x2}px;
  /* Was a stale hardcoded 120px; use the shared (now-corrected) header height
     constant so this stays in sync with CheckSMSStep's identical pattern. */
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
`;

const QRCodeWrapper = styled.div`
  background-color: #f7f7f3;
  padding: ${Spacing.x2}px;
  border-radius: 8px;
  margin-bottom: ${Spacing.x2}px;
`;

// Uses the shared Boris result-screen title scale (rg-result-h), consistent
// with not-eligible / eligibility-result / cap-reached / email-sent / check-sms.
const StyledTitle = styled(ResultTitle)`
  text-align: center;
  max-width: 400px;
  margin-bottom: ${Spacing.x1_5}px;
`;

const SearchAppText = styled(Text)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: ${Spacing.x1}px;
  color: ${Colors.foregroundFaint};
`;

const LogoImage = styled.img`
  height: 22px;
  width: auto;
  vertical-align: middle;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 260px;
  margin: ${Spacing.x3}px 0;
  gap: ${Spacing.x2}px;
  color: ${Colors.foregroundFaint};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${Colors.foregroundDisabled};
  }
`;

const SMSModal = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.x2}px;
`;

const ModalContent = styled.div`
  background: white;
  padding: 40px 32px;
  border-radius: 16px;
  max-width: 440px;
  width: 100%;
  text-align: center;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${Colors.foreground};
`;

const ModalDescription = styled(Text)`
  margin-bottom: 32px;
  color: ${Colors.foreground};
`;

const InputWrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 8px;
`;

const InputLabel = styled.label<{ $hasError?: boolean; $hasValue?: boolean }>`
  position: absolute;
  left: 16px;
  top: ${({ $hasValue }) => ($hasValue ? '-8px' : '50%')};
  transform: ${({ $hasValue }) => ($hasValue ? 'none' : 'translateY(-50%)')};
  font-size: ${({ $hasValue }) => ($hasValue ? '12px' : '16px')};
  color: ${({ $hasError }) => ($hasError ? Colors.error : Colors.primary)};
  background: white;
  padding: 0 4px;
  pointer-events: none;
  transition: all 0.2s ease;
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  padding-right: 48px;
  margin-top: 16px;
  border: 2px solid ${({ $hasError }) => ($hasError ? Colors.error : Colors.primary)};
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: ${({ $hasError }) => ($hasError ? Colors.error : Colors.primary)};
  }

  &::placeholder {
    color: transparent;
  }
`;

const ErrorIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  background: ${Colors.error};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: bold;
`;

const ErrorText = styled.span`
  margin-bottom: 8px;
  display: block;
  color: ${Colors.error};
  font-size: 14px;
  text-align: left;
  min-height: 20px;
`;

const SubmitButton = styled(Button)`
  margin-top: 40px;
  width: 100%;
`;

const DisclaimerWrapper = styled.div`
  margin-top: 32px;
`;

const DisclaimerText = styled(Text)`
  white-space: pre-wrap;
  line-height: 1.5;

  a {
    color: ${Colors.primary};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

// Format phone number as (XXX) XXX-XXXX
const formatPhoneDisplay = (digits: string): string => {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const DownloadAppQRCodeStep: React.FC = () => {
  const { t } = useTranslation();
  const { trackView, track } = useAnalytics();
  const { getOtpLink } = useOtpLink();
  const { sendDownloadSMS } = useDownloadSMS();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState(''); // Raw digits only
  const [phoneError, setPhoneError] = useState('');
  const [isTouched, setIsTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Integrate useAppFlow for flow management
  useAppFlow(STEP_KEYS.DOWNLOAD_APP_QR);

  useEffect(() => {
    trackView({ event: VIEW_DOWNLOAD_APP_QR });
  }, [trackView]);

  const getDownloadLink = (): string => {
    const link = getOtpLink();
    if (!link) {
      return env.BRANCH_URL;
    }
    return link;
  };

  const handleTextMeLink = () => {
    track({ event: TEXT_ME_LINK });
    setIsModalOpen(true);
    setPhoneDigits('');
    setPhoneError('');
    setIsTouched(false);
  };

  // Validate and return error message
  const validatePhone = (digits: string): string => {
    if (!digits) {
      return t('form.validations.required');
    }
    if (digits.length !== 10) {
      return t('form.validations.phone');
    }
    return '';
  };

  // Handle input change - extract digits and format display
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Extract only digits
    const digits = input.replace(/\D/g, '').slice(0, 10);
    setPhoneDigits(digits);

    // If touched, validate on change
    if (isTouched) {
      setPhoneError(validatePhone(digits));
    }
  };

  // Handle blur - validate and show error
  const handleInputBlur = () => {
    setIsTouched(true);
    setPhoneError(validatePhone(phoneDigits));
  };

  const handleSendSMS = async () => {
    setIsTouched(true);
    const error = validatePhone(phoneDigits);
    setPhoneError(error);

    if (error) {
      inputRef.current?.focus();
      return;
    }

    // Format phone number to standard format (just digits)
    const formattedPhone = phoneDigits;

    // Send SMS with download link
    sendDownloadSMS('submitDownloadAppPopup', formattedPhone, (success) => {
      if (success) {
        setIsModalOpen(false);
      }
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPhoneDigits('');
    setPhoneError('');
    setIsTouched(false);
  };

  const formattedPhone = formatPhoneDisplay(phoneDigits);

  return (
    <Container>
      <QRCodeWrapper>
        <QRCode
          value={getDownloadLink()}
          size={150}
          bgColor="#F7F7F3"
          aria-label={t('qr-code.aria-label')}
        />
      </QRCodeWrapper>

      <CenteredColumn span={8}>
        <StyledTitle size="h2" as="h1">
          <Trans i18nKey="qr-code.title" />
        </StyledTitle>
      </CenteredColumn>

      <SearchAppText size="p2">
        {t('qr-code.search-before')} &nbsp;
        <LogoImage src={LogoIcon} alt="Happy Hearts" /> &nbsp;
        {t('qr-code.search-after')}
      </SearchAppText>

      <Divider role="none">
        <span>{t('qr-code.or')}</span>
      </Divider>

      <Button
        variant="action-secondary"
        text={t('qr-code.text-me-link')}
        onClick={handleTextMeLink}
      />

      {/* SMS Modal */}
      <SMSModal
        $open={isModalOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-modal-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleModalClose();
          }
        }}
      >
        <ModalContent>
          <CloseButton onClick={handleModalClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseButton>

          <ModalTitle id="sms-modal-title">{t('qr-code.modal-title')}</ModalTitle>
          <ModalDescription size="p2" centered>
            {t('qr-code.modal-description')}
          </ModalDescription>

          <InputWrapper>
            <StyledInput
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              id="phone-input"
              value={formattedPhone}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder=" "
              aria-label={t('qr-code.phone-label')}
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'phone-error' : undefined}
              $hasError={!!phoneError}
            />
            <InputLabel htmlFor="phone-input" $hasError={!!phoneError} $hasValue={!!phoneDigits}>
              {t('qr-code.phone-label')}
            </InputLabel>
            {phoneError && <ErrorIcon aria-hidden="true">!</ErrorIcon>}
          </InputWrapper>
          <ErrorText id="phone-error" role="alert">
            {phoneError || ''}
          </ErrorText>

          <SubmitButton
            variant="brand-primary"
            text={t('qr-code.modal-send')}
            onClick={handleSendSMS}
          />

          <DisclaimerWrapper>
            <DisclaimerText size="p2" centered>
              <Trans
                i18nKey="qr-code.modal-disclaimer"
                components={{
                  1: (
                    <a
                      href="https://www.helloheart.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  2: (
                    <a
                      href="https://www.helloheart.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </DisclaimerText>
          </DisclaimerWrapper>
        </ModalContent>
      </SMSModal>
    </Container>
  );
};

export { DownloadAppQRCodeStep };
